import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TodoService } from '@/services/todoService';
import { todosQueryRoot } from './useTodos';
import { useCompany } from '@/contexts/CompanyContext';

// FIN-578 PR2 — wraps the single-item "Send to client" action. `isPending` drives the
// button's disabled/spinner state (AC49); ConsultantTodoCard calls this itself (not a
// shared page-level instance), so pending state is naturally scoped per card, not global.
//
// Refetches on BOTH success and failure (`onSettled`, not `onSuccess`) — a failed send is
// most often a lost race (someone else already sent it), and the card should show what's
// actually true server-side afterward, not a stale "Internal" badge next to an error the
// consultant has to mentally reconcile themselves. `TodoApiClient.redirectTodo`'s own
// `handleError()` call already toasts the specific backend message (e.g. "already sent to
// the client"); the refetch is what makes the card's own state agree with that toast
// instead of just sitting there disagreeing with it.
//
// FIN-875 — invalidates the `['todos']` ROOT, not `['todos', companyId]`. `companyId` is still
// taken so callers keep expressing which company they act on, but a card cannot know which
// lists are currently mounted: the cross-company admin list is keyed `['todos','all']`, so a
// per-company invalidation would leave the just-sent row on screen. Matching on the root keeps
// every present and future list correct without each new action hook having to remember.
export function useSendToClientMutation(companyId: string | undefined) {
  const queryClient = useQueryClient();
  // FIN-877 — which surface this send came from, read once here rather than threaded through
  // every card. Advisory: the server verifies it against the caller's real platform_role.
  const { isPlatformAdminView } = useCompany();

  return useMutation({
    mutationFn: (todoId: string) =>
      TodoService.redirectTodo(todoId, { crossCompanyView: isPlatformAdminView }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: todosQueryRoot });
    },
  });
}

// FIN-578 PR3 — the group header's "Send N to client" bulk action (F25). Unlike the
// single-item mutation above, the backend never throws on a partial batch (AC47/EC22, a
// concurrently resolved member) — it always returns 200 with { sentIds, failedIds } — so
// there's no error path here to toast from; the caller (ConsultantTodoGroupCard) inspects
// the resolved result itself to decide whether to show a "Sent X of Y" toast. Same
// `onSettled` refetch as the single mutation: a shrunk/emptied group re-derives from the
// refreshed todos list (ConsultantTodoList's own grouping), not from any bespoke logic here.
export function useSendGroupToClientMutation(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { isPlatformAdminView } = useCompany();

  return useMutation({
    mutationFn: (todoIds: string[]) =>
      TodoService.redirectTodos(todoIds, { crossCompanyView: isPlatformAdminView }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: todosQueryRoot });
    },
  });
}
